import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { PartialDeep } from 'type-fest';
import type { Filter, Sort } from 'contexts/preferences/listing-params/types';
import type SearchPrediction from 'data/search-predictions';
import { convertSearchSuggestionToSearchPrediction } from 'data/search-predictions';
import { useThemeContext } from 'contexts/theme';
import { useUserContext } from 'contexts/user';
import { getDeviceIdFromCookie } from 'utils/device-id';
import usePopularCities from 'hooks/use-popular-cities';
import { getCityHeroImageUrls } from 'components/home-page/local-focus/hero-search/hero-images';
import { formatCityProvinceLabel } from 'utils/standardized-city-name';
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import SmartSearchCard from 'components/atomic/organisms/smart-search-card';
import type { SmartSearchSection } from 'components/atomic/organisms/smart-search-card';
import { buildClassName } from 'utils/build-class-name';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';
import {
  GET_INSPIRED_SECTION_ID,
  MATCHING_LISTINGS_SECTION_ID,
  MATCHING_LOCATIONS_SECTION_ID,
  MIN_INSPIRATION_CHIPS,
  MIN_PLACE_FRAGMENT_LENGTH,
  POPULAR_AREAS_SECTION_ID,
  SMART_SEARCH_LISTING_ICON,
  SMART_SEARCH_PLACE_ICON,
} from './smart-search.constants';
import { useSmartSearchState } from './use-smart-search-state';
import { useSmartSearchResultsCount } from './use-smart-search-results-count';
import type { CountListingsForFilter } from './use-smart-search-results-count';
import { useLocationSuggestions } from './use-location-suggestions';
import { SMART_SEARCH_TAGS } from 'utils/natural-language-filter-parser';
import { extractPlaceFragment, fromAppliedFilter, shuffled } from './smart-search.utils';
import {
  completePlaceFragment,
  placeLabel,
  queryNamesPlace,
  removePlaceFromQuery,
} from './place-selection';
import { parseNaturalLanguageFiltersRemote } from './remote-parse';
import { logSmartSearchQuery } from './log-query';
import type { SmartSearchAppliedLocation } from './log-query';
import { classifyListingMatch } from './listing-match';
import styles from './smart-search-panel.module.scss';

const SMART_SEARCH_LOG_SOURCE = 'area-page-smart-search';

export type SmartSearchPanelProps = {
  /** Applies the parsed filters to the page's search. */
  onFiltersApplied: (filter: PartialDeep<Filter>) => void;
  /** Applies a sort the query asked for ("cheapest first"). */
  onSortApplied?: (sort: Sort) => void;
  /** Navigates to another area; used by the popular-area chips. */
  onLocationSelected?: (searchPrediction: SearchPrediction) => void;
  areaName?: string;
  areaSlug?: string;
  /** Listing count for the filters already applied, shown until a query narrows it. */
  resultsCount?: number;
  /**
   * Counts the listings the pending query would match, so the submit button can
   * preview the result before the user commits to it.
   */
  onCountListings?: CountListingsForFilter;
  /** Collapses the panel (Escape, or after a search is applied). */
  onDismiss?: () => void;
  /** Seeds the query so text survives collapsing and reopening the panel. */
  initialQuery?: string;
  /** Seeds the selected smart tags so they survive reopening the panel. */
  initialTags?: string[];
  /**
   * Filters already applied to the page. Shown as chips on open so the panel
   * reflects the current search, and cleared alongside the query on Clear.
   */
  initialFilter?: PartialDeep<Filter>;
  /** Reports the query so the collapsed search field can echo it. */
  onQueryChanged?: (query: string) => void;
  autoFocus?: boolean;
  className?: string;
};

/** Chip id for the place picked from the suggestions, distinct from parsed ones. */
const SELECTED_PLACE_TOKEN_ID = 'selectedPlace';

/** Chip id for the area the page is already scoped to, shown by default. */
const CURRENT_AREA_TOKEN_ID = 'currentArea';

/** Chip id prefix for a selected smart tag, distinct from parsed filter chips. */
const SMART_TAG_TOKEN_PREFIX = 'smartTag:';

/**
 * A "Get inspired" chip. Both sources are canonical tags, so there is no glyph
 * to show: a tag has nothing contextual to pick from, and one repeated glyph
 * across the whole section would say nothing.
 */
interface InspirationChip {
  /** Raw canonical tag name — what go-search filters on and the toggle key. */
  id: string;
  label: string;
}

// Popular-area thumbnails reuse the same S3 city photos as the search dropdown.
function cityThumbnailUrl(urlPath: string): string {
  return getCityHeroImageUrls(urlPath.replace(/-real-estate$/, '')).sm;
}

// Popular city labels arrive as "toronto, ON - City": drop the trailing kind
// and fix the casing the insights API returns.
function cityChipLabel(label: string): string {
  return formatCityProvinceLabel((label.split('- ')[0] ?? label).trim());
}

/**
 * Area page smart search: free-text query in, filters out.
 *
 * A location named in the query moves the page: on submit it is navigated to,
 * the same path the popular-area chips and the location search take. It is
 * never applied as a `Filter` value — the area page scopes its search by area,
 * so a city filter would be ignored and the results would silently stay put.
 *
 * The place itself is picked, not guessed. As soon as the query starts naming
 * one, matching locations replace the suggestion sections and choosing one
 * completes the word and pins it as a chip, so "which Toronto" is settled while
 *
 * Because this panel resolves places itself, it parses with
 * `locationIdentification` off — see `remote-parse`.
 */
export function SmartSearchPanel({
  onFiltersApplied,
  onSortApplied,
  onLocationSelected,
  areaName,
  areaSlug,
  resultsCount,
  onCountListings,
  onDismiss,
  initialQuery,
  initialTags,
  initialFilter,
  onQueryChanged,
  autoFocus = false,
  className,
}: SmartSearchPanelProps) {
  const { locale } = useRouter();
  const { t } = useTranslation(Namespaces.SEARCH);
  const { tenantId } = useThemeContext();
  const { user } = useUserContext();
  const popularCities = usePopularCities();

  const [pickedPlace, setPickedPlace] = useState<SearchPrediction | null>(null);

  const initialFilters = useMemo(() => fromAppliedFilter(initialFilter), [initialFilter]);

  const {
    query,
    onQueryChange,
    status,
    tokens: parsedTokens,
    unparsed,
    confidence,
    removeToken,
    smartTags,
    matchedTags,
    matchedTagDetails,
    smartTagStatus,
    addTag,
    removeTag,
    parsedFilters,
    filterPatch,
    apply,
    clear: clearQuery,
    undo,
    redo,
    canUndo,
    canRedo,
    isApplyDisabled,
    hasApplicableQuery,
    isResolving,
  } = useSmartSearchState({
    onApplyFilters: onFiltersApplied,
    onApplySort: onSortApplied,
    locale,
    initialQuery,
    initialTags,
    initialFilters,
    // Parsing runs server-side: the LLM steps that fill gaps need credentials
    // that are not in the browser bundle.
    parse: parseNaturalLanguageFiltersRemote,
  });

  // Report through an effect so undo/redo and clear are covered too, not just typing.
  useEffect(() => {
    onQueryChanged?.(query);
  }, [query, onQueryChanged]);

  // A pick only counts while the query still names it, so editing the text away
  // from "Toronto" drops the chip rather than leaving it pinned to a word that
  // is no longer written — and typing the name back restores the same pick.
  const selectedPlace = useMemo(() => {
    if (pickedPlace && queryNamesPlace(query, placeLabel(pickedPlace))) {
      return pickedPlace;
    }
    return null;
  }, [pickedPlace, query]);

  const selectedPlaceLabel = selectedPlace ? placeLabel(selectedPlace) : null;

  // Nothing left to disambiguate once a place is chosen: the fragment now reads
  // back as the name that was picked, and re-offering the list would fight the
  // chip that the pick just produced.
  const isSettledParse = status === 'ready' || status === 'error';
  const placeFragment = useMemo(() => {
    if (selectedPlaceLabel) {
      return null;
    }
    const prepositioned = extractPlaceFragment(query);
    if (prepositioned) {
      return prepositioned;
    }
    if (isSettledParse && !hasApplicableQuery) {
      const trimmed = query.trim();
      return trimmed.length >= MIN_PLACE_FRAGMENT_LENGTH ? trimmed : null;
    }
    return null;
  }, [query, selectedPlaceLabel, isSettledParse, hasApplicableQuery]);

  const {
    locations: locationSuggestions,
    listings: listingSuggestions,
  } = useLocationSuggestions(placeFragment);

  // Counted where the search would land, not where it is now: once a place is
  // picked, submitting moves there, so the current area's total would be a
  // count for a page the button is about to navigate away from.
  const previewCount = useSmartSearchResultsCount({
    filterPatch,
    areaSlug: selectedPlace?.slug,
    appliedCount: resultsCount,
    countListings: onCountListings,
  });

  const tagTokens = useMemo(
    () => smartTags.map(tag => ({
      id: `${SMART_TAG_TOKEN_PREFIX}${tag}`,
      label: capitalizeWords(tag),
      tone: 'accent' as const,
    })),
    [smartTags]
  );

  // The area this page is already scoped to, shown as the first chip so the
  // panel states where the search sits. Go-Search hands areas back as
  // "Toronto, ON"; the province is implied by the page, so only the name shows.
  const currentAreaLabel = useMemo(() => {
    const trimmed = areaName?.trim();
    if (!trimmed) {
      return null;
    }
    return (trimmed.split(',')[0] ?? trimmed).trim() || null;
  }, [areaName]);

  const tokens = useMemo(() => {
    // A place picked in the query supersedes the current-area chip: it is where
    // a submit would navigate, whereas the area chip only states where the
    // search sits now. Only the picked one carries a prediction to navigate to.
    if (selectedPlaceLabel) {
      return [
        ...parsedTokens.filter(token => !token.isLocation),
        {
          id: SELECTED_PLACE_TOKEN_ID,
          icon: SMART_SEARCH_PLACE_ICON,
          label: selectedPlaceLabel,
          tone: 'accent' as const,
        },
        ...tagTokens,
      ];
    }
    // No place picked yet: lead with the current area (read-only — you cannot
    // leave the page you are on by dismissing a chip) and drop any parsed
    // location so the two do not name the same place twice.
    if (currentAreaLabel) {
      return [
        {
          id: CURRENT_AREA_TOKEN_ID,
          icon: SMART_SEARCH_PLACE_ICON,
          label: currentAreaLabel,
          tone: 'accent' as const,
          isRemovable: false,
        },
        ...parsedTokens.filter(token => !token.isLocation),
        ...tagTokens,
      ];
    }
    return [...parsedTokens, ...tagTokens];
  }, [parsedTokens, selectedPlaceLabel, tagTokens, currentAreaLabel]);

  // A static set of canonical tags to browse. Shuffled once per mount for
  // variety, but it does not react to the query — the query's matched tags flow
  // to the filter chips instead.
  const shuffledTags = useMemo(() => shuffled(SMART_SEARCH_TAGS), []);

  const inspirations = useMemo<InspirationChip[]>(
    () => shuffledTags
      .slice(0, MIN_INSPIRATION_CHIPS)
      .map(tag => ({ id: tag, label: capitalizeWords(tag) })),
    [shuffledTags]
  );

  const sections = useMemo<SmartSearchSection[]>(() => {
    // Answering "which place did you mean" is the only thing worth doing while
    // a place is half-typed, so the browsing sections step aside for it rather
    // than pushing the answer below the fold.
    const matchingSections: SmartSearchSection[] = [];
    if (onLocationSelected && listingSuggestions.length > 0) {
      matchingSections.push({
        id: MATCHING_LISTINGS_SECTION_ID,
        title: 'Matching listings',
        icon: SMART_SEARCH_LISTING_ICON,
        layout: 'list',
        headerAction: (
          <span className={styles.matchCount}>
            {`${listingSuggestions.length} ${listingSuggestions.length === 1 ? 'match' : 'matches'}`}
          </span>
        ),
        items: listingSuggestions.map(prediction => ({
          id: prediction.id,
          label: prediction.description,
          icon: SMART_SEARCH_LISTING_ICON,
        })),
      });
    }

    if (locationSuggestions.length > 0) {
      matchingSections.push({
        id: MATCHING_LOCATIONS_SECTION_ID,
        title: 'Matching locations',
        icon: SMART_SEARCH_PLACE_ICON,
        layout: 'list',
        headerAction: (
          <span className={styles.matchCount}>
            {`${locationSuggestions.length} ${locationSuggestions.length === 1 ? 'match' : 'matches'}`}
          </span>
        ),
        items: locationSuggestions.map(prediction => ({
          id: prediction.id,
          label: prediction.description,
          caption: prediction.label ?? undefined,
          icon: SMART_SEARCH_PLACE_ICON,
        })),
      });
    }

    if (matchingSections.length > 0) {
      return matchingSections;
    }

    const result: SmartSearchSection[] = [];

    if (onLocationSelected && popularCities.length > 0) {
      result.push({
        id: POPULAR_AREAS_SECTION_ID,
        title: 'Popular areas',
        icon: '🌟',
        isCollapsible: true,
        rows: 1,
        // No caption: these are all cities, so the "- City" suffix is noise that
        // would double the height of a row that is meant to stay compact.
        items: popularCities.map(city => ({
          id: String(city.identifier),
          label: cityChipLabel(city.label),
          imageUrl: city.urlPath ? cityThumbnailUrl(city.urlPath) : undefined,
        })),
      });
    }

    result.push({
      id: GET_INSPIRED_SECTION_ID,
      title: 'Get inspired',
      icon: '💡',
      rows: 2,
      // Recessive next to the outlined area chips: there are a lot of these and
      // they are hints, not the primary control.
      pillVariant: 'subtle',
      items: inspirations.map(({ id, label }) => ({ id, label })),
    });

    return result;
  }, [popularCities, onLocationSelected, inspirations, locationSuggestions, listingSuggestions]);

  const handleSuggestionSelect = useCallback(async (sectionId: string, suggestionId: string) => {
    if (sectionId === MATCHING_LISTINGS_SECTION_ID) {
      const prediction = listingSuggestions.find(entry => entry.id === suggestionId);
      if (!prediction || !onLocationSelected) {
        return;
      }

      // Logging listing suggestion
      const trimmedQuery = query.trim();
      if (trimmedQuery) {
        const toLoggedListing = (entry: SearchPrediction) => ({
          id: entry.id,
          description: entry.description,
          urlPath: entry.urlPath,
        });
        logSmartSearchQuery({
          tenantId,
          userId: user?.id != null ? String(user.id) : undefined,
          sessionId: getDeviceIdFromCookie() || undefined,
          locale,
          source: SMART_SEARCH_LOG_SOURCE,
          createdAt: new Date().toISOString(),
          originAreaSlug: areaSlug || undefined,
          areaName: areaName || undefined,
          rawQuery: trimmedQuery,
          parseStatus: status,
          confidence,
          matchType: classifyListingMatch(trimmedQuery, prediction),
          listingsSuggested: listingSuggestions.map(toLoggedListing),
          appliedListing: toLoggedListing(prediction),
          locationStatus: 'listing',
        });
      }
      onLocationSelected(prediction);
      onDismiss?.();
      return;
    }

    if (sectionId === MATCHING_LOCATIONS_SECTION_ID) {
      const prediction = locationSuggestions.find(entry => entry.id === suggestionId);
      if (!prediction || !placeFragment) {
        return;
      }
      // Finish the word they were typing rather than replacing the sentence:
      // the query stays theirs, and the chip explains what the pick resolved to.
      onQueryChange(completePlaceFragment(query, placeFragment, placeLabel(prediction)));
      setPickedPlace(prediction);
      return;
    }

    if (sectionId === GET_INSPIRED_SECTION_ID) {
      const inspiration = inspirations.find(entry => entry.id === suggestionId);
      if (!inspiration) {
        return;
      }
      if (smartTags.includes(inspiration.id)) {
        removeTag(inspiration.id);
      } else {
        addTag(inspiration.id);
      }
      return;
    }

    const city = popularCities.find(entry => String(entry.identifier) === suggestionId);
    if (!city || !onLocationSelected) {
      return;
    }
    const converted = await convertSearchSuggestionToSearchPrediction(city);
    // The converter hands back the raw suggestion if it fails to resolve.
    if (converted && 'id' in converted && 'group' in converted) {
      onLocationSelected(converted as SearchPrediction);
      onDismiss?.();
    }
  }, [query, onQueryChange, inspirations, popularCities, onLocationSelected, onDismiss,
    locationSuggestions, listingSuggestions, placeFragment, smartTags, addTag, removeTag,
    tenantId, user, locale, areaSlug, areaName, status, confidence]);

  const handleTokenRemove = useCallback((tokenId: string) => {
    if (tokenId.startsWith(SMART_TAG_TOKEN_PREFIX)) {
      removeTag(tokenId.slice(SMART_TAG_TOKEN_PREFIX.length));
      return;
    }
    if (tokenId !== SELECTED_PLACE_TOKEN_ID) {
      removeToken(tokenId);
      return;
    }
    // Take the name out of the query too. Dropping only the chip would leave
    // the text still naming the place, which reads as a place being typed and
    // brings the suggestion list straight back.
    if (selectedPlaceLabel) {
      onQueryChange(removePlaceFromQuery(query, selectedPlaceLabel));
    }
    setPickedPlace(null);
  }, [removeToken, removeTag, onQueryChange, query, selectedPlaceLabel]);

  const handleClear = useCallback(() => {
    setPickedPlace(null);
    clearQuery();
  }, [clearQuery]);

  const handleSubmit = useCallback(() => {
    // Filters first: navigation carries the current `Filter` into the target
    // URL, so they have to be on `listingParams` before the route changes.
    apply();
    onDismiss?.();

    let appliedLocation: SmartSearchAppliedLocation | null = null;
    if (onLocationSelected && selectedPlace) {
      appliedLocation = { slug: selectedPlace.slug, areaName: placeLabel(selectedPlace), source: 'picked' };
    }

    const trimmedQuery = query.trim();
    if (trimmedQuery) {
      const appliedFilters: PartialDeep<Filter> = { ...filterPatch };
      delete appliedFilters.smartTagNames;
      logSmartSearchQuery({
        tenantId,
        userId: user?.id != null ? String(user.id) : undefined,
        sessionId: getDeviceIdFromCookie() || undefined,
        locale,
        source: SMART_SEARCH_LOG_SOURCE,
        createdAt: new Date().toISOString(),
        originAreaSlug: areaSlug || undefined,
        areaName: areaName || undefined,
        rawQuery: trimmedQuery,
        unparsed,
        confidence,
        parsedFilters,
        appliedFilters,
        smartTagsMatched: matchedTagDetails,
        smartTagsApplied: smartTags,
        // Tags the user added by hand, i.e. applied but not matched from the text.
        smartTagsManual: smartTags.filter(tag => !matchedTags.includes(tag)),
        smartTagsStatus: smartTagStatus,
        locationsSuggested: locationSuggestions.map(prediction => ({
          id: prediction.id,
          slug: prediction.slug,
          label: prediction.description,
          urlPath: prediction.urlPath,
        })),
        appliedLocation,
        locationStatus: appliedLocation?.source
          ?? (locationSuggestions.length > 0 ? 'suggested' : 'none'),
        parseStatus: status,
        hadError: status === 'error' || smartTagStatus === 'error',
      });
    }

    if (onLocationSelected && selectedPlace) {
      onLocationSelected(selectedPlace);
    }
  }, [
    apply, onDismiss, onLocationSelected, selectedPlace, locationSuggestions,
    query, filterPatch, parsedFilters, unparsed, confidence, matchedTagDetails,
    smartTags, matchedTags, smartTagStatus, status, tenantId, user, locale,
    areaSlug, areaName,
  ]);

  const namedPlace = selectedPlaceLabel;
  const hasLocationTerm = Boolean(namedPlace);

  // The leftover text is only "not applied" when the /tags API found no smart
  // tag for it either — a match means the residual was understood as a tag, so
  // the message would contradict the chip it produced. Held back while a lookup
  // is still resolving so it does not flash before the tags land.
  const unparsedText = !isResolving && matchedTags.length === 0 ? unparsed : null;

  return (
    <div className={buildClassName(styles.root, className)} data-testid="AreaPageV2-SmartSearch">
      <SmartSearchCard
        value={query}
        onValueChange={onQueryChange}
        onSubmit={handleSubmit}
        tokens={tokens}
        onTokenRemove={handleTokenRemove}
        isLoadingTokens={isResolving}
        onClear={handleClear}
        sections={sections}
        onSuggestionSelect={handleSuggestionSelect}
        smartTagsDisclaimer={smartTags.length > 0
          ? t(LOCALE_SEARCH_PATHS.SMART_SEARCH_SMART_TAGS_DISCLAIMER, {
            defaultValue:
              'Search results are based on MLS listing descriptions written by agents. If a feature isn\'t mentioned, it may still be available.',
          })
          : null}
        locationNote={hasLocationTerm
          ? `Searching moves to ${namedPlace} when you apply.`
          : null}
        unparsedText={unparsedText}
        placeholder="Describe your dream home..."
        submitLabel={
          previewCount === undefined
            ? 'Search'
            : `View ${previewCount.toLocaleString()} ${previewCount === 1 ? 'listing' : 'listings'}`
        }
        isSubmitDisabled={isApplyDisabled && !hasLocationTerm}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onDismiss={onDismiss}
        autoFocus={autoFocus}
      />
    </div>
  );
}

export default SmartSearchPanel;
